
import {
  DIGITAL_HUMAN_LOADING,
  DIGITAL_HUMAN_SETTING,
  HYGEN_SESSION_ID,
  HYGEN_SESSION_INFO,
  INIT_DIGITAL_HUMAN,
  INIT_HYGEN_SESSION,
  INIT_HYGEN_SESSION_ERROR,
  LIVEAVATAR_SESSION,
  START_HYGEN_SESSION,
  STOP_HYGEN_SESSION
} from "@/store/modules/digital_human/constants";
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ROOT_ERROR, ROOT_LOADING } from "@/store/modules/root/constants";
import AppResourseNotFound from "@/components/shared/AppResourseNotFound.vue";
import { SessionEvent } from "@heygen/liveavatar-web-sdk";

export default Vue.extend({
  name: "DigitalHuman",
  components: {
    AppResourseNotFound
  },
  data() {
    return {
      streamReadyEmitted: false
    };
  },
  props: {
    interview_finished: {
      type: Boolean,
      default: false
    },
    bot_ans_loading: {
      type: Boolean,
      default: false
    }
  },
  async created() {
    sessionStorage.removeItem("hygen_session_id");
    this.init_digital_human();
    await this.initialize_session();
  },
  watch: {
    interview_finished() {
      if (this.interview_finished) {
        this.stop_hygen_session({
          session_id: this.hygen_session_id
        });
      }
    }
  },
  computed: {
    ...mapGetters({
      get_root_error: ROOT_ERROR,
      get_root_loading: ROOT_LOADING
    }),
    ...mapGetters("digital_human", {
      digital_human_setting: DIGITAL_HUMAN_SETTING,
      init_hygen_session_error: INIT_HYGEN_SESSION_ERROR,
      digital_human_loading: DIGITAL_HUMAN_LOADING,
      hygen_session_id: HYGEN_SESSION_ID,
      hygen_session_info: HYGEN_SESSION_INFO,
      liveavatar_session: LIVEAVATAR_SESSION
    })
  },
  methods: {
    ...mapActions("digital_human", {
      init_digital_human: INIT_DIGITAL_HUMAN,
      init_hygen_session: INIT_HYGEN_SESSION,
      start_hygen_session: START_HYGEN_SESSION,
      stop_hygen_session: STOP_HYGEN_SESSION
    }),
    ...mapMutations({
      set_root_error: ROOT_ERROR,
      set_root_loading: ROOT_LOADING
    }),
    ...mapMutations("digital_human", {
      set_init_hygen_session_error: INIT_HYGEN_SESSION_ERROR,
      set_hygen_session_id: HYGEN_SESSION_ID,
      set_hygen_session_info: HYGEN_SESSION_INFO
    }),
    resolveAvatarId(): string {
      return String(
        process.env.VUE_APP_LIVEAVATAR_AVATAR_ID ||
          process.env.VUE_APP_HYGEN_AVATAR_ID ||
          ""
      ).trim();
    },
    bindStreamReadyHandler() {
      const session = this.liveavatar_session;
      if (!session) return;

      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        if (this.streamReadyEmitted) return;
        this.streamReadyEmitted = true;

        const mediaElement = this.$refs.mediaElement as HTMLVideoElement;
        if (mediaElement) {
          session.attach(mediaElement);
        }
        this.$emit("ready");
      });
    },
    async initialize_session() {
      this.streamReadyEmitted = false;
      const requestedAvatarId = this.resolveAvatarId();

      if (!requestedAvatarId) {
        this.set_init_hygen_session_error(
          "LiveAvatar avatar ID is not configured. Set VUE_APP_LIVEAVATAR_AVATAR_ID to a valid UUID from app.liveavatar.com."
        );
        this.set_root_error("LiveAvatar avatar ID is not configured");
        return;
      }

      const resp = await this.init_hygen_session({
        quality: "high",
        avatar_id: requestedAvatarId,
        voice: {
          voice_id: this.digital_human_setting.bot_voice_id
        }
      });

      const session_id = resp?.session_id;
      if (!resp || !session_id) {
        this.set_init_hygen_session_error(
          "Error initializing LiveAvatar session. Verify your LiveAvatar API key and avatar ID."
        );
        this.set_root_error("Error in initializing Bot session");
        return;
      }

      this.set_hygen_session_id(session_id);
      this.set_hygen_session_info(resp);
      this.bindStreamReadyHandler();

      const started = await this.start_hygen_session({ session_id });
      if (!started) {
        this.set_init_hygen_session_error("Failed to start Bot session");
        this.set_root_error("Failed to start Bot session");
        await this.stop_hygen_session({ session_id });
      }
    },
    hideElement(element: HTMLElement) {
      element.classList.add("hide");
      element.classList.remove("show");
    },
    showElement(element: HTMLElement) {
      element.classList.add("show");
      element.classList.remove("hide");
    },
    removeBackground() {
      this.render_canvas();
    },
    render_canvas() {
      const canvasElement = this.$refs.canvasElement as HTMLCanvasElement;
      const mediaElement = this.$refs.mediaElement as HTMLVideoElement;
      if (!canvasElement || !mediaElement) return;

      const curRenderID = Math.trunc(Math.random() * 1000000000);
      const ctx = canvasElement.getContext("2d", {
        willReadFrequently: true
      }) as CanvasRenderingContext2D | null;

      function processFrame() {
        if (!ctx) return;

        canvasElement.width = mediaElement.videoWidth;
        canvasElement.height = mediaElement.videoHeight;
        canvasElement.style.maxHeight = window.innerHeight + "px";
        canvasElement.style.maxWidth = "100%";
        ctx.drawImage(
          mediaElement,
          0,
          0,
          canvasElement.width,
          canvasElement.height
        );

        const imageData = ctx.getImageData(
          0,
          0,
          canvasElement.width,
          canvasElement.height
        );
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const red = data[i];
          const green = data[i + 1];
          const blue = data[i + 2];
          if (green > 90 && red < 90 && blue < 90) {
            data[i + 3] = 0;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        requestAnimationFrame(processFrame);
      }

      processFrame();
      this.hideElement(mediaElement);
      this.showElement(canvasElement);
    }
  },
  beforeDestroy() {
    if (this.hygen_session_id) {
      this.stop_hygen_session({
        session_id: this.hygen_session_id
      });
    }
  }
});
